import { jsx as _jsx } from "react/jsx-runtime";
import * as d3 from "d3";
import { format } from "date-fns";
import React, { useEffect, useRef } from "react";
import { useAssetPowerSocTrendsQuery } from "../../services/query/project-asset-details.query";
import useAppConfigStore from "../../store/useAppConfigStore";
const AssetDetailsLineChart = ({ assetId, height, projectId, width }) => {
    const svgRef = useRef(null);
    const margin = { bottom: 60, left: 80, right: 80, top: 50 };
    const currentDay = new Date();
    const formattedTodayDate = format(currentDay, "yyyy-MM-dd");
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const { data: { assetTrendsDetails = [] } = {} } = useAssetPowerSocTrendsQuery(projectId, formattedTodayDate, assetId, 60);
    useEffect(() => {
        drawChart();
    }, [assetId, projectId, assetTrendsDetails]);
    const drawChart = () => {
        // Clear previous content
        d3.select(svgRef.current).selectAll("*").remove();
    };
    if (!assetTrendsDetails.length)
        return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[40vh]", children: _jsx("span", { children: "No data available for the selected asset." }) }));
    const assetTrendsData = assetTrendsDetails !== undefined ? assetTrendsDetails[0].trends : [];
    /**
     * Parses timestamp strings into Date objects for D3.js chart rendering.
     *
     * For projects with showCestTimezone enabled (Malaga, Tiwa Dyke, etc.):
     * - API sends timestamps in project's local timezone (e.g., "00:00:00" = midnight Amsterdam)
     * - We parse them using UTC (Date.UTC) to create timezone-neutral Date objects
     * - ALL users worldwide see the same times (project timezone, not their browser's)
     * - Example: "00:00:00" displays as "12 AM" for users in Tokyo, New York, London, etc.
     *
     * Why UTC?: Using Date.UTC ensures the absolute moment in time is identical for all users.
     * Without UTC, new Date(2025, 11, 18, 0, 0, 0) would represent different absolute moments
     * in Tokyo (UTC+9) vs New York (UTC-5), causing the chart to show different times.
     *
     * Supports two formats:
     * 1. Time-only: "00:00:00", "15:30:45" (Malaga project)
     * 2. Full timestamp: "2025-12-18T00:00:00.000" (Tiwa Dyke project)
     *
     * @param timestampStr - Timestamp string from API
     * @returns Date object with timezone-neutral representation (using UTC)
     */
    const parseTimestamp = (timestampStr) => {
        if (!timestampStr)
            return new Date();
        // When showCestTimezone is TRUE:
        // API sends timestamps in project's local timezone (Amsterdam, London, etc.)
        // We display these times as-is, without timezone conversion
        if (appConfig?.release_1_1?.showCestTimezone) {
            try {
                let datePart;
                let timePart;
                // Handle time-only format: "00:00:00" or "15:30:45"
                if (!timestampStr.includes("T")) {
                    // For time-only format, use the same date that was sent in the API query
                    // (formattedTodayDate) to ensure consistency between the query date and parsed timestamps.
                    // The API query date is based on the user's browser date, and time-only timestamps
                    // are returned for that specific date, so we combine them accordingly.
                    datePart = formattedTodayDate;
                    timePart = timestampStr;
                }
                else {
                    // Full timestamp format: "2025-12-18T00:00:00.000"
                    [datePart, timePart] = timestampStr.split("T");
                }
                // Parse components directly without timezone conversion
                const [year, month, day] = datePart.split("-").map(Number);
                const [hours, minutes, seconds] = timePart.split(":").map((v) => Math.floor(Number(v)));
                // Create Date object using UTC to ensure timezone-neutral representation
                // This ensures ALL users worldwide see the same time values on the chart
                // e.g., "00:00:00" displays as midnight (12 AM) for everyone, not shifted by their timezone
                return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
            }
            catch (error) {
                return new Date();
            }
        }
        // Legacy mode: Parse as local browser time
        return new Date(timestampStr);
    };
    // Expect full timestamp in ISO format from API when showCestTimezone flag is true
    const trendsData = appConfig?.release_1_1?.showCestTimezone
        ? assetTrendsData
        : assetTrendsData.map((data) => {
            let timestamp = data.timestamp || "";
            if (!timestamp.includes("T")) {
                const todayPrefix = new Date().toISOString().split("T")[0] + "T";
                timestamp = todayPrefix + timestamp;
            }
            timestamp = timestamp.replace(/Z$/, "");
            return {
                ...data,
                timestamp
            };
        });
    // Create powerData array
    const powerData = trendsData
        .filter((data) => data.power !== undefined) // Filter out objects without power value
        .map((data) => ({
        parsedDate: parseTimestamp(data.timestamp || ""),
        timestamp: data.timestamp,
        value: data.power ?? undefined // Default to undefined if power is undefined
    }));
    // Create socData array
    const socData = trendsData
        .filter((data) => data.soc !== undefined)
        .map((data) => ({
        parsedDate: parseTimestamp(data.timestamp || ""),
        timestamp: data.timestamp,
        value: data.soc ?? undefined // Default to undefined if power is undefined
    }));
    // Setup SVG
    const svg = d3
        .select(svgRef.current)
        .style("max-width", "100%")
        .style("min-width", "100%")
        .style("max-height", "100%")
        .style("position", "relative")
        .style("overflow", "auto")
        .attr("viewBox", `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`); // Add viewBox
    const chartArea = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);
    // Add clipPath to make the chart scrollable
    chartArea.append("clipPath").attr("id", "clip").append("rect").attr("width", width).attr("height", height);
    const clippedArea = chartArea.append("g").attr("clip-path", "url(#clip)");
    // Combine all parsed dates for domain calculation
    const allParsedDates = [...powerData.map((d) => d.parsedDate), ...socData.map((d) => d.parsedDate)];
    const xScale = d3
        .scaleTime()
        .domain(d3.extent(allParsedDates)) // Use pre-parsed dates
        .range([0, width]);
    const yScaleSOC = d3.scaleLinear().domain([0, 100]).range([height, 0]);
    const maxPower = powerData.length > 0 ? d3.max(powerData, (d) => d.value) || 100 : 100;
    const yScalePower = d3.scaleLinear().domain([0, maxPower]).range([height, 0]);
    // Create axes
    const xAxis = d3
        .axisBottom(xScale)
        .tickFormat((d) => d3.utcFormat("%I %p")(d)) // Use UTC 12-hour format with AM/PM
        .ticks(d3.utcHour.every(2)); // Use UTC intervals to match UTC formatting
    const yAxisLeft = d3.axisLeft(yScaleSOC).ticks(5);
    const yAxisRight = d3
        .axisRight(yScalePower)
        .ticks(5)
        .tickValues(d3.range(0, maxPower + 1, maxPower / 5));
    // Remove existing axes to avoid duplication
    chartArea.select(".x-axis").remove();
    // Add X-axis
    chartArea
        .append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0, ${height})`)
        .call(xAxis)
        .selectAll("text")
        .style("text-anchor", "middle")
        .attr("font-size", "12px")
        .attr("fill", "grey"); // Make scaling values grey
    chartArea
        .select(".x-axis .domain") // Hide the axis line
        .attr("stroke", "none");
    // Add Y-axis (left) for SOC
    chartArea
        .append("g")
        .attr("class", "y-axis-left")
        .call(yAxisLeft)
        .attr("font-size", "12px")
        .selectAll("text")
        .attr("fill", "grey");
    chartArea
        .select(".y-axis-left .domain") // Hide the axis line
        .attr("stroke", "none");
    // Add Y-axis (right) for Power
    chartArea
        .append("g")
        .attr("class", "y-axis-right")
        .attr("transform", `translate(${width}, 0)`)
        .call(yAxisRight)
        .attr("font-size", "12px")
        .selectAll("text")
        .attr("fill", "grey");
    chartArea
        .select(".y-axis-right .domain") // Hide the axis line
        .attr("stroke", "none");
    // Add X-axis label
    chartArea
        .append("text")
        .attr("fill", "black")
        .attr("transform", `translate(${width / 2}, ${height + margin.bottom - 20})`) // Position at the center below the x-axis
        .style("text-anchor", "middle")
        .style("font", "Sans-serif")
        .style("font-size", "14px")
        .style("font-weight", "500")
        .text("Timeline");
    // Y-axis labels
    chartArea
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", 0 - margin.left + 20)
        .attr("x", 0 - height / 2)
        .attr("dy", "1em")
        .style("text-anchor", "middle")
        .style("font", "Sans-serif")
        .style("font-size", "14px")
        .style("font-weight", "500")
        .attr("fill", "black")
        .text("SOC (%)");
    chartArea
        .append("text")
        .attr("transform", "rotate(-90)")
        .attr("y", width + margin.right - 30)
        .attr("x", 0 - height / 2)
        .attr("dy", "1em")
        .style("text-anchor", "middle")
        .style("font-family", "Sans-serif") // Ensure font-family is set to Sans-serif
        .style("font-size", "14px")
        .style("font-weight", "500")
        .attr("fill", "black")
        .text("ESS Power (kW)");
    // Create SOC line
    const lineSOC = d3
        .line()
        .x((d) => xScale(d.parsedDate)) // Use pre-parsed date
        .y((d) => yScaleSOC(d.value)) // Use yScaleSOC for soc values
        .curve(d3.curveLinear);
    if (socData.length > 1) {
        clippedArea
            .append("path")
            .datum(socData)
            .attr("fill", "none")
            .attr("stroke", "#1AC69E") // Color for SOC line
            .attr("stroke-width", 2)
            .attr("d", lineSOC);
        // Add circles to connect every point
        socData.forEach((dataPoint) => {
            clippedArea
                .append("circle")
                .attr("cx", xScale(dataPoint.parsedDate)) // Use pre-parsed date
                .attr("cy", yScaleSOC(dataPoint.value)) // Use yScaleSOC for SOC value
                .attr("r", 3) // Radius of the point
                .attr("fill", "#1AC69E"); // Color for the point
        });
    }
    else if (socData.length === 1) {
        // Render a single point instead of a line
        clippedArea
            .append("circle")
            .attr("cx", xScale(socData[0].parsedDate)) // Use pre-parsed date
            .attr("cy", yScaleSOC(socData[0].value)) // Use yScaleSOC for SOC value
            .attr("r", 3) // Radius of the point
            .attr("fill", "#1AC69E"); // Color for the point
    }
    // Create Power line
    const linePower = d3
        .line()
        .x((d) => xScale(d.parsedDate)) // Use pre-parsed date
        .y((d) => yScalePower(d.value)) // Use yScalePower for power values
        .curve(d3.curveLinear);
    if (powerData.length > 1) {
        clippedArea
            .append("path")
            .datum(powerData)
            .attr("fill", "none")
            .attr("stroke", "#FB5A00") // Color for Power line
            .attr("stroke-width", 2)
            .attr("d", linePower);
        // Add circles to connect every point
        powerData.forEach((dataPoint) => {
            clippedArea
                .append("circle")
                .attr("cx", xScale(dataPoint.parsedDate)) // Use pre-parsed date
                .attr("cy", yScalePower(dataPoint.value)) // Use yScaleSOC for SOC value
                .attr("r", 3) // Radius of the point
                .attr("fill", "#FB5A00"); // Color for the point
        });
    }
    else if (powerData.length === 1) {
        // Render a single point instead of a line
        clippedArea
            .append("circle")
            .attr("cx", xScale(powerData[0].parsedDate)) // Use pre-parsed date
            .attr("cy", yScalePower(powerData[0].value)) // Use yScaleSOC for SOC value
            .attr("r", 3) // Radius of the point
            .attr("fill", "#FB5A00"); // Color for the point
    }
    // Add legend on top right
    const legend = chartArea
        .append("g")
        .attr("class", "legend")
        .attr("transform", `translate(${width - 200}, -30)`); // Adjust position to top-right
    const legendData = [
        { color: "#1AC69E", label: "SOC (%)" },
        { color: "#FB5A00", label: "ESS Power (kW)" }
    ];
    const legendItems = legend
        .selectAll(".legend-item")
        .data(legendData)
        .enter()
        .append("g")
        .attr("class", "legend-item")
        .attr("transform", (d, i) => `translate(${i * 100}, 0)`); // Place items in the same line
    legendItems
        .append("rect")
        .attr("width", 12)
        .attr("height", 2)
        .attr("fill", (d) => d.color);
    legendItems
        .append("text")
        .attr("x", 18)
        .attr("y", 1)
        .attr("dy", "0.35em")
        .style("font-size", "11px") // Reduced font size for cleaner appearance
        .style("font-weight", "normal") // Set font weight to normal for less boldness
        .text((d) => d.label);
    // Add grid lines for better readability
    chartArea
        .selectAll(".grid-line-y")
        .data(yScaleSOC.ticks(5))
        .enter()
        .append("line")
        .attr("class", "grid-line-y")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", (d) => yScaleSOC(d))
        .attr("y2", (d) => yScaleSOC(d))
        .attr("stroke", "#e0e0e0")
        .attr("stroke-width", 0.5)
        .attr("stroke-dasharray", "2,2");
    return (_jsx("div", { children: _jsx("svg", { "data-test": "asset-details-linechart-svg", ref: svgRef }) }));
};
export default AssetDetailsLineChart;
